<template>
  <div class="min-h-screen bg-gradient-to-b from-slate-50 to-white dark:from-gray-900 dark:to-gray-800">
    <!-- Modern Navigation -->
    <nav class="fixed top-0 w-full bg-white/90 dark:bg-gray-900/90 backdrop-blur-md z-50 border-b border-gray-100 dark:border-gray-800">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between items-center h-16">
          <!-- Logo -->
          <div class="flex items-center space-x-3">
            <img
              src="/img/logo.webp"
              alt="MailerX"
              class="h-10"
            />
          </div>

          <!-- Navigation Links -->
          <div class="hidden md:flex items-center space-x-8">
            <a href="#features" class="text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white font-medium transition-colors">{{ $t('welcome.nav.features') }}</a>
            <a href="#pricing" class="text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white font-medium transition-colors">{{ $t('welcome.nav.pricing') }}</a>
            <router-link :to="{ name: 'api-docs', params: { locale: currentLocale } }" class="text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white font-medium transition-colors">{{ $t('welcome.nav.documentation') }}</router-link>
          </div>

          <!-- Auth Buttons -->
          <div class="flex items-center space-x-4">
            <!-- Language Switcher -->
            <LanguageSwitcher :compact="true" position="right" />

            <router-link
              :to="{ name: 'login', params: { locale: currentLocale } }"
              class="text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white font-medium transition-colors"
            >
              {{ $t('welcome.nav.sign_in') }}
            </router-link>
            <router-link
              :to="{ name: 'register', params: { locale: currentLocale } }"
              class="px-5 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg font-medium hover:shadow-lg transition-all duration-200 transform hover:-translate-y-0.5"
            >
              {{ $t('welcome.nav.get_started') }}
            </router-link>
          </div>
        </div>
      </div>
    </nav>

    <!-- Hero Section -->
    <section class="relative pt-32 pb-20 overflow-hidden">
      <!-- Background Effects -->
      <div class="absolute inset-0 pointer-events-none">
        <div class="absolute top-20 left-20 w-72 h-72 bg-purple-300 dark:bg-purple-600 rounded-full mix-blend-multiply dark:mix-blend-screen filter blur-xl opacity-20 dark:opacity-10 animate-blob"></div>
        <div class="absolute top-40 right-20 w-72 h-72 bg-indigo-300 dark:bg-indigo-600 rounded-full mix-blend-multiply dark:mix-blend-screen filter blur-xl opacity-20 dark:opacity-10 animate-blob animation-delay-2000"></div>
        <div class="absolute bottom-20 left-1/2 w-72 h-72 bg-pink-300 dark:bg-pink-600 rounded-full mix-blend-multiply dark:mix-blend-screen filter blur-xl opacity-20 dark:opacity-10 animate-blob animation-delay-4000"></div>
      </div>

      <div class="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center">
          <!-- Badge -->
          <div class="inline-flex items-center px-4 py-2 bg-indigo-100 dark:bg-indigo-900/50 rounded-full mb-8">
            <SparklesIcon class="w-5 h-5 text-indigo-600 dark:text-indigo-400 mr-2" />
            <span class="text-sm font-medium text-indigo-700 dark:text-indigo-300">{{ $t('welcome.hero.badge') }}</span>
          </div>

          <!-- Headline -->
          <h1 class="text-5xl md:text-7xl font-bold text-gray-900 dark:text-white mb-6">
            {{ $t('welcome.hero.title') }}
            <span class="block bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
              {{ $t('welcome.hero.title_highlight') }}
            </span>
          </h1>

          <!-- Description -->
          <p class="text-xl text-gray-700 dark:text-gray-300 max-w-3xl mx-auto mb-10">
            {{ $t('welcome.hero.description') }}
          </p>

          <!-- CTA Button -->
          <div class="flex justify-center mb-12">
            <router-link
              :to="{ name: 'register', params: { locale: currentLocale } }"
              class="px-8 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-semibold text-lg hover:shadow-xl transition-all duration-200 transform hover:-translate-y-1"
            >
              {{ $t('welcome.hero.cta_start') }}
              <ArrowRightIcon class="inline w-5 h-5 ml-2" />
            </router-link>
          </div>
        </div>
      </div>
    </section>

    <!-- Features Grid -->
    <section id="features" class="py-20 bg-white dark:bg-gray-900">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-16">
          <h2 class="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            {{ $t('welcome.features.title') }}
          </h2>
          <p class="text-xl text-gray-600 dark:text-gray-300">
            {{ $t('welcome.features.subtitle') }}
          </p>
        </div>

        <div class="grid md:grid-cols-3 gap-8">
          <!-- Feature 1 -->
          <div class="group p-8 bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-2xl hover:shadow-xl transition-all duration-300 border border-transparent dark:border-indigo-800/30">
            <div class="w-14 h-14 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
              <BoltIcon class="w-8 h-8 text-white" />
            </div>
            <h3 class="text-2xl font-bold text-gray-900 dark:text-white mb-3">{{ $t('welcome.features.lightning_fast.title') }}</h3>
            <p class="text-gray-700 dark:text-gray-300">{{ $t('welcome.features.lightning_fast.description') }}</p>
          </div>

          <!-- Feature 2 -->
          <div class="group p-8 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-2xl hover:shadow-xl transition-all duration-300 border border-transparent dark:border-green-800/30">
            <div class="w-14 h-14 bg-gradient-to-br from-green-600 to-emerald-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
              <ChartBarIcon class="w-8 h-8 text-white" />
            </div>
            <h3 class="text-2xl font-bold text-gray-900 dark:text-white mb-3">{{ $t('welcome.features.real_time_analytics.title') }}</h3>
            <p class="text-gray-700 dark:text-gray-300">{{ $t('welcome.features.real_time_analytics.description') }}</p>
          </div>

          <!-- Feature 3 -->
          <div class="group p-8 bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 rounded-2xl hover:shadow-xl transition-all duration-300 border border-transparent dark:border-orange-800/30">
            <div class="w-14 h-14 bg-gradient-to-br from-orange-600 to-red-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
              <CpuChipIcon class="w-8 h-8 text-white" />
            </div>
            <h3 class="text-2xl font-bold text-gray-900 dark:text-white mb-3">{{ $t('welcome.features.smart_automation.title') }}</h3>
            <p class="text-gray-700 dark:text-gray-300">{{ $t('welcome.features.smart_automation.description') }}</p>
          </div>

          <!-- Feature 4 -->
          <div class="group p-8 bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 rounded-2xl hover:shadow-xl transition-all duration-300 border border-transparent dark:border-blue-800/30">
            <div class="w-14 h-14 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
              <PuzzlePieceIcon class="w-8 h-8 text-white" />
            </div>
            <h3 class="text-2xl font-bold text-gray-900 dark:text-white mb-3">{{ $t('welcome.features.seamless_integrations.title') }}</h3>
            <p class="text-gray-700 dark:text-gray-300">{{ $t('welcome.features.seamless_integrations.description') }}</p>
          </div>

          <!-- Feature 5 -->
          <div class="group p-8 bg-gradient-to-br from-pink-50 to-rose-50 dark:from-pink-900/20 dark:to-rose-900/20 rounded-2xl hover:shadow-xl transition-all duration-300 border border-transparent dark:border-pink-800/30">
            <div class="w-14 h-14 bg-gradient-to-br from-pink-600 to-rose-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
              <ShieldCheckIcon class="w-8 h-8 text-white" />
            </div>
            <h3 class="text-2xl font-bold text-gray-900 dark:text-white mb-3">{{ $t('welcome.features.enterprise_security.title') }}</h3>
            <p class="text-gray-700 dark:text-gray-300">{{ $t('welcome.features.enterprise_security.description') }}</p>
          </div>

          <!-- Feature 6 -->
          <div class="group p-8 bg-gradient-to-br from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-purple-900/20 rounded-2xl hover:shadow-xl transition-all duration-300 border border-transparent dark:border-violet-800/30">
            <div class="w-14 h-14 bg-gradient-to-br from-violet-600 to-purple-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
              <UserGroupIcon class="w-8 h-8 text-white" />
            </div>
            <h3 class="text-2xl font-bold text-gray-900 dark:text-white mb-3">{{ $t('welcome.features.support_24_7.title') }}</h3>
            <p class="text-gray-700 dark:text-gray-300">{{ $t('welcome.features.support_24_7.description') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Pricing Section -->
    <section id="pricing" class="py-20 bg-gray-50 dark:bg-gray-800">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-16">
          <h2 class="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            {{ $t('welcome.pricing.title') }}
          </h2>
          <p class="text-xl text-gray-700 dark:text-gray-300">
            {{ $t('welcome.pricing.subtitle') }}
          </p>
        </div>

        <div class="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          <!-- Starter Plan -->
          <div class="bg-white dark:bg-gray-800/50 dark:backdrop-blur-sm rounded-2xl shadow-lg p-8 hover:shadow-xl transition-shadow border border-transparent dark:border-gray-700">
            <h3 class="text-2xl font-bold text-gray-900 dark:text-white mb-2">{{ $t('welcome.pricing.starter.name') }}</h3>
            <div class="mb-6">
              <span class="text-4xl font-bold text-gray-900 dark:text-white">{{ $t('welcome.pricing.starter.price') }}</span>
              <span class="text-gray-500 dark:text-gray-400">{{ $t('welcome.pricing.starter.period') }}</span>
            </div>
            <ul class="space-y-3 mb-8">
              <li class="flex items-start">
                <CheckIcon class="w-5 h-5 text-green-500 mr-2 mt-0.5" />
                <span class="text-gray-700 dark:text-gray-300">{{ $t('welcome.pricing.starter.features.contacts') }}</span>
              </li>
              <li class="flex items-start">
                <CheckIcon class="w-5 h-5 text-green-500 mr-2 mt-0.5" />
                <span class="text-gray-700 dark:text-gray-300">{{ $t('welcome.pricing.starter.features.emails') }}</span>
              </li>
              <li class="flex items-start">
                <CheckIcon class="w-5 h-5 text-green-500 mr-2 mt-0.5" />
                <span class="text-gray-700 dark:text-gray-300">{{ $t('welcome.pricing.starter.features.templates') }}</span>
              </li>
            </ul>
            <button class="w-full py-3 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
              {{ $t('welcome.pricing.starter.cta') }}
            </button>
          </div>

          <!-- Pro Plan (Popular) -->
          <div class="bg-gradient-to-br from-indigo-600 to-purple-600 rounded-2xl shadow-xl p-8 text-white relative transform scale-105">
            <div class="absolute -top-4 left-1/2 transform -translate-x-1/2">
              <span class="bg-gradient-to-r from-yellow-400 to-orange-400 text-white px-4 py-1 rounded-full text-sm font-medium">
                {{ $t('welcome.pricing.professional.badge') }}
              </span>
            </div>
            <h3 class="text-2xl font-bold mb-2">{{ $t('welcome.pricing.professional.name') }}</h3>
            <div class="mb-6">
              <span class="text-4xl font-bold">{{ $t('welcome.pricing.professional.price') }}</span>
              <span class="text-indigo-100">{{ $t('welcome.pricing.professional.period') }}</span>
            </div>
            <ul class="space-y-3 mb-8">
              <li class="flex items-start">
                <CheckIcon class="w-5 h-5 text-white mr-2 mt-0.5" />
                <span>{{ $t('welcome.pricing.professional.features.contacts') }}</span>
              </li>
              <li class="flex items-start">
                <CheckIcon class="w-5 h-5 text-white mr-2 mt-0.5" />
                <span>{{ $t('welcome.pricing.professional.features.emails') }}</span>
              </li>
              <li class="flex items-start">
                <CheckIcon class="w-5 h-5 text-white mr-2 mt-0.5" />
                <span>{{ $t('welcome.pricing.professional.features.automation') }}</span>
              </li>
              <li class="flex items-start">
                <CheckIcon class="w-5 h-5 text-white mr-2 mt-0.5" />
                <span>{{ $t('welcome.pricing.professional.features.support') }}</span>
              </li>
            </ul>
            <button class="w-full py-3 bg-white text-indigo-600 rounded-lg font-medium hover:bg-gray-100 transition-colors">
              {{ $t('welcome.pricing.professional.cta') }}
            </button>
          </div>

          <!-- Enterprise Plan -->
          <div class="bg-white dark:bg-gray-800/50 dark:backdrop-blur-sm rounded-2xl shadow-lg p-8 hover:shadow-xl transition-shadow border border-transparent dark:border-gray-700">
            <h3 class="text-2xl font-bold text-gray-900 dark:text-white mb-2">{{ $t('welcome.pricing.enterprise.name') }}</h3>
            <div class="mb-6">
              <span class="text-4xl font-bold text-gray-900 dark:text-white">{{ $t('welcome.pricing.enterprise.price') }}</span>
              <span class="text-gray-500 dark:text-gray-400">{{ $t('welcome.pricing.enterprise.period') }}</span>
            </div>
            <ul class="space-y-3 mb-8">
              <li class="flex items-start">
                <CheckIcon class="w-5 h-5 text-green-500 mr-2 mt-0.5" />
                <span class="text-gray-700 dark:text-gray-300">{{ $t('welcome.pricing.enterprise.features.contacts') }}</span>
              </li>
              <li class="flex items-start">
                <CheckIcon class="w-5 h-5 text-green-500 mr-2 mt-0.5" />
                <span class="text-gray-700 dark:text-gray-300">{{ $t('welcome.pricing.enterprise.features.emails') }}</span>
              </li>
              <li class="flex items-start">
                <CheckIcon class="w-5 h-5 text-green-500 mr-2 mt-0.5" />
                <span class="text-gray-700 dark:text-gray-300">{{ $t('welcome.pricing.enterprise.features.integrations') }}</span>
              </li>
              <li class="flex items-start">
                <CheckIcon class="w-5 h-5 text-green-500 mr-2 mt-0.5" />
                <span class="text-gray-700 dark:text-gray-300">{{ $t('welcome.pricing.enterprise.features.manager') }}</span>
              </li>
            </ul>
            <button class="w-full py-3 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
              {{ $t('welcome.pricing.enterprise.cta') }}
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="py-20 bg-gradient-to-r from-indigo-600 to-purple-600">
      <div class="max-w-4xl mx-auto text-center px-4">
        <h2 class="text-4xl font-bold text-white mb-6">
          {{ $t('welcome.cta.title') }}
        </h2>
        <p class="text-xl text-indigo-100 mb-8">
          {{ $t('welcome.cta.subtitle') }}
        </p>
        <router-link
          :to="{ name: 'register' }"
          class="inline-block px-8 py-4 bg-white text-indigo-600 rounded-xl font-semibold text-lg hover:shadow-xl transition-all duration-200 transform hover:-translate-y-1"
        >
          {{ $t('welcome.cta.button') }}
        </router-link>
        <p class="text-indigo-100 mt-4">
          {{ $t('welcome.cta.note') }}
        </p>
      </div>
    </section>

    <!-- Footer -->
    <footer class="bg-gray-900 text-gray-300 py-12">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="grid md:grid-cols-4 gap-8">
          <!-- Brand -->
          <div>
            <div class="flex items-center space-x-3 mb-4">
              <img
                src="/img/logo.webp"
                alt="MailerX"
                class="h-10"
              />
            </div>
            <p class="text-gray-400">
              {{ $t('welcome.footer.tagline') }}
            </p>
          </div>

          <!-- Quick Links -->
          <div>
            <h3 class="text-white font-semibold mb-4">{{ $t('welcome.footer.product') }}</h3>
            <ul class="space-y-2">
              <li><a href="#features" class="hover:text-white transition-colors">{{ $t('welcome.nav.features') }}</a></li>
              <li><a href="#pricing" class="hover:text-white transition-colors">{{ $t('welcome.nav.pricing') }}</a></li>
              <li><router-link :to="{ name: 'api-docs', params: { locale: currentLocale } }" class="hover:text-white transition-colors">{{ $t('welcome.nav.documentation') }}</router-link></li>
              <li><router-link :to="{ name: 'login', params: { locale: currentLocale } }" class="hover:text-white transition-colors">{{ $t('welcome.nav.sign_in') }}</router-link></li>
            </ul>
          </div>

          <!-- Legal -->
          <div>
            <h3 class="text-white font-semibold mb-4">{{ $t('welcome.footer.legal') }}</h3>
            <ul class="space-y-2">
              <li><a href="#" class="hover:text-white transition-colors">{{ $t('welcome.footer.privacy_policy') }}</a></li>
              <li><a href="#" class="hover:text-white transition-colors">{{ $t('welcome.footer.terms_of_service') }}</a></li>
              <li><a href="#" class="hover:text-white transition-colors">{{ $t('welcome.footer.gdpr') }}</a></li>
            </ul>
          </div>

          <!-- Contact -->
          <div>
            <h3 class="text-white font-semibold mb-4">{{ $t('welcome.footer.contact') }}</h3>
            <ul class="space-y-2">
              <li><a href="mailto:support@mailerx.com" class="hover:text-white transition-colors">support@mailerx.com</a></li>
            </ul>
          </div>
        </div>

        <div class="border-t border-gray-800 mt-12 pt-8 text-center">
          <p class="text-gray-400">
            {{ $t('welcome.footer.copyright', { year: new Date().getFullYear() }) }}
          </p>
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import LanguageSwitcher from '@/components/common/LanguageSwitcher.vue'
import {
  ArrowRightIcon,
  SparklesIcon,
  BoltIcon,
  ChartBarIcon,
  CpuChipIcon,
  PuzzlePieceIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  CheckIcon
} from '@heroicons/vue/24/outline'

const { t } = useI18n()
const route = useRoute()
const currentLocale = computed(() => route.params.locale || 'en')
</script>

<style scoped>
@keyframes blob {
  0% {
    transform: translate(0px, 0px) scale(1);
  }
  33% {
    transform: translate(30px, -50px) scale(1.1);
  }
  66% {
    transform: translate(-20px, 20px) scale(0.9);
  }
  100% {
    transform: translate(0px, 0px) scale(1);
  }
}

.animate-blob {
  animation: blob 7s infinite;
}

.animation-delay-2000 {
  animation-delay: 2s;
}

.animation-delay-4000 {
  animation-delay: 4s;
}
</style>
